import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash';
import classes from './OffersSelectedFilters.scss'

import CancelIcon from '@mui/icons-material/Cancel';
import EntityChips from 'cityads-v3/components/EntitySelectorWithChips/EntityChips'
import IconButton from '@mui/material/IconButton'
import Paper from 'cityads-v3/components/Paper/Paper'
import { useFilterLists } from './OffersSelectedFilters.hooks';
import { LocationParams, useOfferFiltersContext } from './OfferFilters.hooks'

type FilterNames = keyof Omit<LocationParams, 'page' | 'limit' | 'order'>

/**
 * @todo вынести HandleDeleteItemFunction и HandleDeleteGroupFunction
 * в отдельный файлик
 */
export type HandleDeleteItemFunction = <K, T extends {
  removeFromArray: (value: K) => void
}>(field: T) => (value: K) => void

export type HandleDeleteGroupFunction = <K, T extends {
  setValue: (value: K) => void
}>(field: T, value: K) => () => void

type OffersSelectedFiltersProps = {
  displayFilters: Partial<Record<FilterNames, boolean>>
  label?: string
}
export default function OffersSelectedFilters({
  displayFilters = {},
  label
}: OffersSelectedFiltersProps): JSX.Element {
  const { t } = useTranslation(['Offers']);

  const { selectedFilters, clearFilters } = useOfferFiltersContext()

  const {
    name,
    tags,
    region_id: regionId,
    direction_and_platform: directionAndPlatform,
    subscription_status: subscriptionStatusId,
    category_id: categoryId,
    traffic_type_id: trafficTypeId,
    tool_id: toolId,
    has_promocode_attribution: hasPromocodeAttribution,
    is_deeplink_enabled: isDeeplinkEnabled,
    has_cart: hasCart,
  } = selectedFilters.getFormData()

  const {
    offerTags,
    offerTagsByCode,
    regionList,
    directionAndPlatformList,
    subscriptionStatusList,
    categoryList,
    trafficTypeList,
    toolList,
  } = useFilterLists()

  const categoryUniqueValues = useMemo(() => {
    const ignoreCategoryIds: number[] = [];
    categoryId.forEach(id => {
      const categoryIds = categoryList[id]?.category_ids;
      if (categoryIds?.length > 1) {
        ignoreCategoryIds.push(...categoryIds.slice(1))
      }
    })

    return categoryId.filter(id => !ignoreCategoryIds.includes(id))
  }, [categoryId, categoryList])

  const handleDeleteItem = useCallback<HandleDeleteItemFunction>(field => values => {
    field.removeFromArray(values);
    selectedFilters.handleSubmit();
  }, [selectedFilters])

  const handleDeleteGroup = useCallback<HandleDeleteGroupFunction>((field, value) => () => {
    field.setValue(value);
    selectedFilters.handleSubmit();
  }, [selectedFilters])

  const showFilters = name && displayFilters.name !== false
    || tags?.length && displayFilters.tags !== false
    || regionId?.length && displayFilters.region_id !== false
    || directionAndPlatform?.length && displayFilters.direction_and_platform !== false
    || subscriptionStatusId?.length && displayFilters.subscription_status !== false
    || categoryUniqueValues?.length && displayFilters.category_id !== false
    || trafficTypeId?.length && displayFilters.traffic_type_id !== false
    || toolId?.length && displayFilters.tool_id !== false
    || hasPromocodeAttribution && displayFilters.has_promocode_attribution !== false
    || isDeeplinkEnabled && displayFilters.is_deeplink_enabled !== false
    || hasCart && displayFilters.has_cart !== false

  return showFilters ? (
    <Paper
      classes={{ root: classes.container }}
      label={label}
    >
      <div className={classes.itemList}>

        {displayFilters.name !== false && (
          <EntityChips
            itemsById={{ 1: { name: `${t('filters.name')}: ${name}` } }}
            value={name ? [1] : undefined}
            onDelete={() => handleDeleteGroup(selectedFilters.fields.name, '')}
            variant="filled"
          />
        )}

        {offerTags?.length && displayFilters.tags !== false ? tags?.map(tagCode => {
          return (
            <EntityChips
              key={tagCode}
              itemsById={{ 1: { name: offerTagsByCode[tagCode]?.translated_name ?? offerTagsByCode[tagCode]?.name } }}
              value={[1]}
              onDelete={() => () => {
                selectedFilters.fields.tags.removeFromArray([tagCode]);
                selectedFilters.handleSubmit()
              }}
              variant="filled"
            />
          );
        }) : null}

        {
          displayFilters.region_id !== false && (
            <EntityChips
              withGrouping
              groupedLabel={t('filters.region_id')}
              itemsById={regionList}
              value={isEmpty(regionList) ? null : regionId}
              onMultiDelete={handleDeleteItem<number[], typeof selectedFilters.fields.region_id>(selectedFilters.fields.region_id)}
              onGroupDelete={handleDeleteGroup(selectedFilters.fields.region_id, [])}
              variant="filled"
            />
          )
        }

        {
          displayFilters.direction_and_platform !== false && (
            <EntityChips
              withGrouping
              groupedLabel={t('filters.offer_type')}
              itemsById={directionAndPlatformList}
              itemsByIdWithParents={directionAndPlatformList as any}
              value={isEmpty(directionAndPlatformList) ? null : directionAndPlatform}
              onMultiDelete={
                handleDeleteItem<string[], typeof selectedFilters.fields.direction_and_platform>(selectedFilters.fields.direction_and_platform)
              }
              onGroupDelete={handleDeleteGroup(selectedFilters.fields.direction_and_platform, [])}
              variant="filled"
            />
          )
        }

        {
          displayFilters.subscription_status !== false && (
            <EntityChips
              withGrouping
              groupedLabel={t('filters.subscription_status')}
              itemsById={subscriptionStatusList}
              value={isEmpty(subscriptionStatusList) ? null : subscriptionStatusId}
              onMultiDelete={
                handleDeleteItem<number[], typeof selectedFilters.fields.subscription_status>(selectedFilters.fields.subscription_status)
              }
              onGroupDelete={handleDeleteGroup(selectedFilters.fields.subscription_status, [])}
              variant="filled"
            />
          )
        }

        {
          displayFilters.category_id !== false && (
            <EntityChips
              withGrouping
              groupedLabel={t('filters.category_id')}
              itemsById={categoryList}
              value={isEmpty(categoryList) ? null : categoryUniqueValues}
              onGroupDelete={handleDeleteGroup(selectedFilters.fields.category_id, [])}
              variant="filled"
            />
          )
        }

        {
          displayFilters.traffic_type_id !== false && (
            <EntityChips
              withGrouping
              groupedLabel={t('filters.traffic_type_id')}
              itemsById={trafficTypeList}
              value={isEmpty(trafficTypeList) ? null : trafficTypeId}
              onMultiDelete={handleDeleteItem<number[], typeof selectedFilters.fields.traffic_type_id>(selectedFilters.fields.traffic_type_id)}
              onGroupDelete={handleDeleteGroup(selectedFilters.fields.traffic_type_id, [])}
              variant="filled"
            />
          )
        }

        {
          displayFilters.tool_id !== false && (
            <EntityChips
              withGrouping
              groupedLabel={t('filters.tool_id')}
              itemsById={toolList}
              value={isEmpty(toolList) ? null : toolId}
              onMultiDelete={handleDeleteItem<number[], typeof selectedFilters.fields.tool_id>(selectedFilters.fields.tool_id)}
              onGroupDelete={handleDeleteGroup(selectedFilters.fields.tool_id, [])}
              variant="filled"
            />

          )
        }

        {
          displayFilters.has_promocode_attribution !== false && (
            <EntityChips
              itemsById={{ 1: { name: t('filters.hasPromocodeAttribution') } }}
              value={hasPromocodeAttribution ? [1] : undefined}
              onDelete={() => handleDeleteGroup(selectedFilters.fields.has_promocode_attribution, false)}
              variant="filled"
            />

          )
        }

        {
          displayFilters.is_deeplink_enabled !== false && (
            <EntityChips
              itemsById={{ 1: { name: t('filters.isDeeplinkEnabled') } }}
              value={isDeeplinkEnabled ? [1] : undefined}
              onDelete={() => handleDeleteGroup(selectedFilters.fields.is_deeplink_enabled, false)}
              variant="filled"
            />

          )
        }

        {
          displayFilters.has_cart !== false && (
            <EntityChips
              itemsById={{ 1: { name: t('filters.hasCart') } }}
              value={hasCart ? [1] : undefined}
              onDelete={() => handleDeleteGroup(selectedFilters.fields.has_cart, false)}
              variant="filled"
            />
          )
        }
      </div >
      <IconButton
        className={classes.resetButton}
        aria-label={t('resetFilters')}
        onClick={clearFilters}
        size="large"
      >
        <CancelIcon />
      </IconButton>
    </Paper >
  ) : null
}
